/**
 * A slippy map drawn to a 2D canvas, shared by the minimap and the world map.
 *
 * Layers, bottom to top: tiles in the game's own style (`tileRenderer.ts`);
 * live radar in broadcast colours; the night side, graded through the three
 * twilights, with cities glowing in it; place names; a TV-forecast weather
 * board — big symbols and temperature badges over the main cities; the points
 * where the sun and moon are overhead; the player.
 */

import { nightShade } from '../core/map/daynight.js';
import { weatherGrid } from '../core/map/weathergrid.js';
import { moonPosition, subsolarPoint, sublunarPoint, sunAltitudeFrom } from '../core/sky/astronomy.js';
import { latToMercatorY, lonToMercatorX, mercatorXToLon, mercatorYToLat } from '../core/geo/mercator.js';
import { drawMoon, drawSun, drawTemperature, drawWeatherSymbol } from './icons.js';
import type { MapTiles } from './mapTiles.js';
import type { Radar } from './radar.js';
import { RADAR_MAX_ZOOM } from './radar.js';
import type { MapLabel } from './tileRenderer.js';
import type { WeatherSamples } from './weatherSamples.js';

export interface MapLayers {
  night: boolean;
  radar: boolean;
  weather: boolean;
  labels: boolean;
}

export interface MapFrame {
  player: { lat: number; lon: number; heading: number };
  date: Date;
  layers: MapLayers;
  /** A chosen destination, drawn as a pin. */
  target?: { lat: number; lon: number };
  /** The way being travelled to it, drawn as a line. */
  route?: ReadonlyArray<{ lat: number; lon: number }>;
  /** Draw the sun and moon markers (the world map does; the minimap does not). */
  bodies?: boolean;
  /** Other players, from the realtime room. */
  others?: ReadonlyArray<{ lat: number; lon: number; heading: number; name: string }>;
  /** Write their names beside them, when zoomed in far enough to read. */
  names?: boolean;
}

const TILE = 256;
const OCEAN = '#6ec3dc';
const LABEL_RANK: Record<string, number> = {
  country: 0, state: 1, capital: 1, city: 2, town: 3, village: 5, suburb: 6, hamlet: 7, locality: 8,
};

export class MapView {
  lat = 57.72;
  lon = 12.94;
  zoom = 5;
  private readonly ctx: CanvasRenderingContext2D;
  private width = 1;
  private height = 1;
  private dpr = 1;
  private readonly nightCanvas = document.createElement('canvas');
  private nightKey = '';

  constructor(
    readonly canvas: HTMLCanvasElement,
    private readonly tiles: MapTiles,
    private readonly radar: Radar,
    private readonly samples: WeatherSamples,
  ) {
    this.ctx = canvas.getContext('2d')!;
  }

  resize(width: number, height: number): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.width = width;
    this.height = height;
    this.canvas.width = Math.round(width * this.dpr);
    this.canvas.height = Math.round(height * this.dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.nightKey = '';
  }

  /** World pixels at the current zoom. */
  private world(lat: number, lon: number): [number, number] {
    const size = TILE * 2 ** this.zoom;
    return [lonToMercatorX(lon) * size, latToMercatorY(lat) * size];
  }

  toScreen(lat: number, lon: number): [number, number] {
    const [cx, cy] = this.world(this.lat, this.lon);
    const [wx, y] = this.world(lat, lon);
    let x = wx;
    const size = TILE * 2 ** this.zoom;
    // Nearest copy across the antimeridian.
    if (x - cx > size / 2) x -= size;
    if (cx - x > size / 2) x += size;
    return [x - cx + this.width / 2, y - cy + this.height / 2];
  }

  toLatLon(sx: number, sy: number): { lat: number; lon: number } {
    const size = TILE * 2 ** this.zoom;
    const [cx, cy] = this.world(this.lat, this.lon);
    const mx = (cx + sx - this.width / 2) / size;
    const my = Math.min(0.9999, Math.max(0.0001, (cy + sy - this.height / 2) / size));
    return { lat: mercatorYToLat(my), lon: ((mercatorXToLon(mx) + 540) % 360) - 180 };
  }

  panBy(dx: number, dy: number): void {
    const c = this.toLatLon(this.width / 2 - dx, this.height / 2 - dy);
    this.lat = Math.max(-84, Math.min(84, c.lat));
    this.lon = c.lon;
  }

  zoomAt(sx: number, sy: number, delta: number): void {
    const before = this.toLatLon(sx, sy);
    this.zoom = Math.max(1.5, Math.min(17, this.zoom + delta));
    const [ax, ay] = this.toScreen(before.lat, before.lon);
    this.panBy(sx - ax, sy - ay);
  }

  draw(frame: MapFrame): void {
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = OCEAN;
    ctx.fillRect(0, 0, this.width, this.height);

    const labels = this.drawTiles();
    if (frame.layers.radar) this.drawRadar();
    if (frame.layers.night) this.drawNight(frame.date, labels);
    if (frame.layers.labels) this.drawLabels(labels, frame.layers.weather);
    if (frame.layers.weather) this.drawWeather(labels, frame.date);
    if (frame.bodies === true) this.drawBodies(frame.date);
    if (frame.route !== undefined && frame.route.length > 1) this.drawRoute(frame.route);
    if (frame.target !== undefined) this.drawTarget(frame.target);
    if (frame.others !== undefined && frame.others.length > 0) this.drawOthers(frame.others, frame.names === true && this.zoom >= 12);
    this.drawPlayer(frame.player);
    this.tiles.pump();
  }

  private tileZoom(): number {
    return Math.max(0, Math.min(16, Math.floor(this.zoom + (this.dpr > 1.25 ? 0.7 : 0.25))));
  }

  private drawTiles(): MapLabel[] {
    const ctx = this.ctx;
    const tz = this.tileZoom();
    const n = 2 ** tz;
    const scale = 2 ** (this.zoom - tz);
    const ts = TILE * scale;
    const [cx, cy] = this.world(this.lat, this.lon);
    const originX = cx / scale - this.width / 2 / scale;
    const originY = cy / scale - this.height / 2 / scale;
    const x0 = Math.floor(originX / TILE), x1 = Math.floor((originX + this.width / scale) / TILE);
    const y0 = Math.max(0, Math.floor(originY / TILE)), y1 = Math.min(n - 1, Math.floor((originY + this.height / scale) / TILE));
    const labels: MapLabel[] = [];
    const midX = (x0 + x1) / 2, midY = (y0 + y1) / 2;

    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const wx = ((tx % n) + n) % n;
        const dx = (tx * TILE - originX) * scale;
        const dy = (ty * TILE - originY) * scale;
        const entry = this.tiles.get(tz, wx, ty);
        if (entry !== undefined) {
          ctx.drawImage(entry.bitmap, dx, dy, ts + 0.6, ts + 0.6);
          labels.push(...entry.labels);
          continue;
        }
        this.tiles.want(tz, wx, ty, Math.hypot(tx - midX, ty - midY));
        // Meanwhile, the nearest cached ancestor, scaled up.
        for (let k = 1; k <= 6 && tz - k >= 0; k++) {
          const parent = this.tiles.get(tz - k, wx >> k, ty >> k);
          if (parent === undefined) continue;
          const sub = TILE / 2 ** k;
          ctx.drawImage(parent.bitmap, (wx - ((wx >> k) << k)) * sub, (ty - ((ty >> k) << k)) * sub, sub, sub, dx, dy, ts + 0.6, ts + 0.6);
          labels.push(...parent.labels);
          break;
        }
        // Always keep a coarse layer warm, so fallbacks exist.
        if (tz > 4) this.tiles.want(tz - 3, wx >> 3, ty >> 3, 100);
      }
    }
    return labels;
  }

  private drawRadar(): void {
    const ctx = this.ctx;
    const rz = Math.min(RADAR_MAX_ZOOM, this.tileZoom());
    const n = 2 ** rz;
    const scale = 2 ** (this.zoom - rz);
    const ts = TILE * scale;
    const [cx, cy] = this.world(this.lat, this.lon);
    const originX = cx / scale - this.width / 2 / scale;
    const originY = cy / scale - this.height / 2 / scale;
    ctx.globalAlpha = 0.88;
    for (let ty = Math.max(0, Math.floor(originY / TILE)); ty <= Math.min(n - 1, Math.floor((originY + this.height / scale) / TILE)); ty++) {
      for (let tx = Math.floor(originX / TILE); tx <= Math.floor((originX + this.width / scale) / TILE); tx++) {
        const img = this.radar.tile(rz, ((tx % n) + n) % n, ty);
        if (img !== undefined) ctx.drawImage(img, (tx * TILE - originX) * scale, (ty * TILE - originY) * scale, ts, ts);
      }
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The night side, recomputed at quarter resolution only when the view or
   * the time has moved enough to matter: the terminator crawls at 15° an hour.
   */
  private drawNight(date: Date, labels: MapLabel[]): void {
    const sub = subsolarPoint(date);
    const w = Math.max(2, Math.ceil(this.width / 4));
    const h = Math.max(2, Math.ceil(this.height / 4));
    const key = `${this.lat.toFixed(3)},${this.lon.toFixed(3)},${this.zoom.toFixed(2)},${w},${h},${Math.floor(date.getTime() / 20_000)}`;
    if (key !== this.nightKey) {
      this.nightKey = key;
      this.nightCanvas.width = w;
      this.nightCanvas.height = h;
      const nctx = this.nightCanvas.getContext('2d')!;
      const image = nctx.createImageData(w, h);
      for (let j = 0; j < h; j++) {
        for (let i = 0; i < w; i++) {
          const p = this.toLatLon((i + 0.5) * 4, (j + 0.5) * 4);
          const a = nightShade(sunAltitudeFrom(sub, p.lat, p.lon));
          const o = (j * w + i) * 4;
          image.data[o] = 12; image.data[o + 1] = 20; image.data[o + 2] = 52; image.data[o + 3] = a * 255;
        }
      }
      nctx.putImageData(image, 0, 0);
    }
    const ctx = this.ctx;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(this.nightCanvas, 0, 0, this.width, this.height);

    // City lights on the night side, brighter for bigger places.
    ctx.globalCompositeOperation = 'lighter';
    for (const l of labels) {
      if (l.population < 5000) continue;
      const dark = nightShade(sunAltitudeFrom(sub, l.lat, l.lon));
      if (dark < 0.2) continue;
      const [x, y] = this.toScreen(l.lat, l.lon);
      const r = Math.min(40, 3 + Math.log10(l.population) * 3.2 * Math.max(0.6, this.zoom / 6));
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(255, 214, 140, ${0.75 * dark})`);
      g.addColorStop(1, 'rgba(255, 170, 80, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  /** The best-known places, most important first, de-duplicated. */
  private ranked(labels: MapLabel[]): MapLabel[] {
    const seen = new Set<string>();
    return labels
      .filter(l => {
        const k = `${l.name}|${l.lat.toFixed(2)}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .sort((a, b) => (LABEL_RANK[a.kind] ?? 9) - (LABEL_RANK[b.kind] ?? 9) || b.population - a.population);
  }

  private drawLabels(labels: MapLabel[], leaveRoomForWeather: boolean): void {
    const ctx = this.ctx;
    const placed: Array<[number, number, number, number]> = [];
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const l of this.ranked(labels).slice(0, 140)) {
      const [x, y] = this.toScreen(l.lat, l.lon);
      if (x < -50 || y < -20 || x > this.width + 50 || y > this.height + 20) continue;
      const big = (LABEL_RANK[l.kind] ?? 9) <= 2;
      const size = big ? 14 : 12;
      ctx.font = `${big ? 700 : 600} ${size}px system-ui, sans-serif`;
      const w = ctx.measureText(l.name).width + 8;
      const box: [number, number, number, number] = [x - w / 2, y - size, x + w / 2, y + size];
      if (placed.some(b => box[0] < b[2] && box[2] > b[0] && box[1] < b[3] && box[3] > b[1])) continue;
      // Weather symbols go on the biggest places; their names go under them.
      if (leaveRoomForWeather && big) continue;
      placed.push(box);
      ctx.lineWidth = 3.5;
      ctx.strokeStyle = 'rgba(255, 252, 244, 0.9)';
      ctx.strokeText(l.name, x, y);
      ctx.fillStyle = big ? '#2b2f36' : '#4a4f58';
      ctx.fillText(l.name, x, y);
    }
  }

  /**
   * The forecast board. Symbols sit on the biggest visible places, spaced so
   * they never crowd, with an evenly spread lattice filling the gaps — open
   * sea, empty country — so the board is never blank where there are no
   * towns.
   */
  private drawWeather(labels: MapLabel[], date: Date): void {
    const spots: Array<{ lat: number; lon: number; name?: string; x: number; y: number }> = [];
    const spacing = 118;
    const fits = (x: number, y: number) =>
      x > 30 && y > 40 && x < this.width - 30 && y < this.height - 40 && spots.every(s => Math.hypot(s.x - x, s.y - y) > spacing);
    for (const l of this.ranked(labels)) {
      if ((LABEL_RANK[l.kind] ?? 9) > 3 || spots.length >= 22) continue;
      const [x, y] = this.toScreen(l.lat, l.lon);
      if (fits(x, y)) spots.push({ lat: l.lat, lon: l.lon, name: l.name, x, y });
    }
    const nw = this.toLatLon(0, 0);
    const se = this.toLatLon(this.width, this.height);
    const degPerPx = 360 / (TILE * 2 ** this.zoom);
    const east = se.lon < nw.lon ? se.lon + 360 : se.lon;
    for (const p of weatherGrid(nw.lon, east, se.lat, nw.lat, degPerPx, 170).points) {
      if (spots.length >= 36) break;
      const [x, y] = this.toScreen(p.lat, p.lon);
      if (fits(x, y)) spots.push({ ...p, x, y });
    }
    this.samples.want(spots);

    const ctx = this.ctx;
    const sub = subsolarPoint(date);
    const size = 44;
    for (const s of spots) {
      const w = this.samples.get(s.lat, s.lon);
      if (w === undefined) continue;
      const night = sunAltitudeFrom(sub, s.lat, s.lon) < 0;
      drawWeatherSymbol(ctx, s.x, s.y - 10, size, w.code, w.cloud, night);
      drawTemperature(ctx, s.x, s.y + 26, w.temperature, size);
      if (s.name !== undefined) {
        ctx.font = '700 12px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.lineWidth = 3.5;
        ctx.strokeStyle = 'rgba(255, 252, 244, 0.92)';
        ctx.strokeText(s.name, s.x, s.y + 48);
        ctx.fillStyle = '#2b2f36';
        ctx.fillText(s.name, s.x, s.y + 48);
      }
    }
  }

  /** Where the sun and moon stand overhead — the heart of the day and the night. */
  private drawBodies(date: Date): void {
    const ctx = this.ctx;
    const sun = subsolarPoint(date);
    const [sx, sy] = this.toScreen(sun.lat, sun.lon);
    const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, 70);
    glow.addColorStop(0, 'rgba(255, 220, 120, 0.55)');
    glow.addColorStop(1, 'rgba(255, 220, 120, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(sx - 70, sy - 70, 140, 140);
    drawSun(ctx, sx, sy, 34);
    const moon = sublunarPoint(date);
    const [mx, my] = this.toScreen(moon.lat, moon.lon);
    const lit = moonPosition(date, moon.lat, moon.lon).illumination;
    ctx.globalAlpha = 0.55 + 0.45 * lit;
    drawMoon(ctx, mx, my, 30);
    ctx.globalAlpha = 1;
  }

  private drawTarget(t: { lat: number; lon: number }): void {
    const ctx = this.ctx;
    const [x, y] = this.toScreen(t.lat, t.lon);
    ctx.shadowColor = 'rgba(20,24,40,0.35)';
    ctx.shadowBlur = 6;
    ctx.fillStyle = '#e2473a';
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.bezierCurveTo(x - 14, y - 16, x - 12, y - 34, x, y - 34);
    ctx.bezierCurveTo(x + 12, y - 34, x + 14, y - 16, x, y);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(x, y - 23, 5, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Other players: a blue dot with a white rim and a tick for their heading —
   * the player's own orange arrow is the one that stands out.
   */
  private drawOthers(others: ReadonlyArray<{ lat: number; lon: number; heading: number; name: string }>, names: boolean): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.font = '700 11px system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    for (const o of others) {
      const [x, y] = this.toScreen(o.lat, o.lon);
      if (x < -20 || y < -20 || x > this.width + 20 || y > this.height + 20) continue;
      const a = (o.heading * Math.PI) / 180;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x + Math.sin(a) * 5, y - Math.cos(a) * 5);
      ctx.lineTo(x + Math.sin(a) * 10, y - Math.cos(a) * 10);
      ctx.stroke();
      ctx.fillStyle = '#2f6fb0';
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      if (names && o.name !== '') {
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(247, 240, 227, 0.9)';
        ctx.strokeText(o.name, x + 9, y);
        ctx.fillStyle = '#2b2f36';
        ctx.fillText(o.name, x + 9, y);
      }
    }
    ctx.restore();
  }

  /** The way the autopilot is going: a blue line with a white casing, as a satnav draws it. */
  private drawRoute(route: ReadonlyArray<{ lat: number; lon: number }>): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const path = () => {
      ctx.beginPath();
      route.forEach((p, i) => {
        const [x, y] = this.toScreen(p.lat, p.lon);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
    };
    path();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 8;
    ctx.stroke();
    path();
    ctx.strokeStyle = '#2f7df0';
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.restore();
  }

  private drawPlayer(p: { lat: number; lon: number; heading: number }): void {
    const ctx = this.ctx;
    const [x, y] = this.toScreen(p.lat, p.lon);
    const a = (p.heading * Math.PI) / 180;
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = 'rgba(240, 119, 47, 0.25)';
    ctx.beginPath();
    ctx.arc(0, 0, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.rotate(a);
    ctx.shadowColor = 'rgba(20,24,40,0.4)';
    ctx.shadowBlur = 4;
    ctx.fillStyle = '#f0772f';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, -11);
    ctx.lineTo(8, 9);
    ctx.lineTo(0, 5);
    ctx.lineTo(-8, 9);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
}
